import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft, Building2, Users, Globe, ScrollText, Settings, Shield,
  UserPlus, Eye, Pause, Play, Archive, UserCog, CreditCard, History,
  StickyNote, UserCircle, LifeBuoy, HeartPulse, Trash2
} from 'lucide-react';

export default function TenantDetail() {
  const { id } = useParams();
  const ubicacion = useLocation();
  const pestanaPedida = new URLSearchParams(ubicacion.search).get('tab');
  const navigate = useNavigate();
  const { startImpersonation, isPlatformOwner } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // S137 (1/N) — desarrollos que este negocio congeló. Solo la plataforma
  // puede devolverlos a la vida.
  const [devsSuspendidos, setDevsSuspendidos] = useState([]);
  const [reactivando, setReactivando] = useState(null);
  const [motivoReactivacion, setMotivoReactivacion] = useState('');
  const [guardandoReactivacion, setGuardandoReactivacion] = useState(false);

  // Add user dialog
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', tenant_role: 'tenant_user' });
  const [addingUser, setAddingUser] = useState(false);

  // Impersonation dialog
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [emailEditUser, setEmailEditUser] = useState(null);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [emailEditReason, setEmailEditReason] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [impTarget, setImpTarget] = useState(null);
  const [impReason, setImpReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  // S42(1/N) — Ficha 360
  const [planHistory, setPlanHistory] = useState([]);
  const [impHistory, setImpHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [savingManager, setSavingManager] = useState(false);
  const [politica, setPolitica] = useState(null);
  const [guardandoPolitica, setGuardandoPolitica] = useState(false);

  // S42(4/N) — health score + tickets
  const [health, setHealth] = useState(null);
  const [tickets, setTickets] = useState([]);

  // S42(5/N) — archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const fetchTenant = async () => {
    try {
      const { data } = await api.get(`/admin/tenants/${id}`);
      setTenant(data);
      cargarPolitica(id);
    } catch (err) {
      setError('Tenant no encontrado');
    } finally { setLoading(false); }
  };

  const fetchFicha360 = async () => {
    const [ph, ih, n, pu, hs, tk] = await Promise.all([
      api.get(`/admin/tenants/${id}/plan-history`).catch(() => ({ data: [] })),
      api.get(`/admin/tenants/${id}/impersonation-history`).catch(() => ({ data: [] })),
      api.get(`/admin/tenants/${id}/notes`).catch(() => ({ data: [] })),
      api.get('/admin/users').catch(() => ({ data: [] })),
      api.get(`/admin/tenants/${id}/health-score`).catch(() => ({ data: null })),
      api.get(`/admin/tickets?tenant_id=${id}`).catch(() => ({ data: [] })),
    ]);
    setPlanHistory(ph.data || []);
    setImpHistory(ih.data || []);
    setNotes(n.data || []);
    setPlatformUsers(pu.data || []);
    setHealth(hs.data || null);
    setTickets(tk.data || []);
  };

  // S137 (1/N) — desarrollos congelados de este negocio.
  const cargarDevsSuspendidos = async () => {
    try {
      const { data } = await api.get(`/admin/tenants/${id}/developments-suspendidos`);
      setDevsSuspendidos(data || []);
    } catch { setDevsSuspendidos([]); }
  };

  const handleReactivar = async () => {
    const texto = motivoReactivacion.trim();
    if (texto.length < 10) {
      toast.error('Escribe el motivo de la reactivación (al menos 10 caracteres).');
      return;
    }
    setGuardandoReactivacion(true);
    try {
      await api.post(`/admin/developments/${reactivando.id}/reactivar`, { motivo: texto });
      toast.success(`«${reactivando.name}» quedó reactivado. El negocio ya puede trabajarlo.`);
      setReactivando(null); setMotivoReactivacion(''); cargarDevsSuspendidos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo reactivar'); }
    finally { setGuardandoReactivacion(false); }
  };

  useEffect(() => { fetchTenant(); fetchFicha360(); cargarDevsSuspendidos(); }, [id]);

  // S109 — qué hace el sistema cuando este negocio topa un límite.
  const cargarPolitica = async (tid) => {
    try {
      const { data } = await api.get(`/admin/tenants/${tid}/politica-de-limites`);
      setPolitica(data);
    } catch { /* si no carga, la tarjeta no se pinta y no estorba */ }
  };

  const cambiarPolitica = async (valor) => {
    setGuardandoPolitica(true);
    try {
      await api.put(`/admin/tenants/${id}/politica-de-limites?politica=${valor}`);
      await cargarPolitica(id);
      toast.success(valor === 'consumo_avisa'
        ? 'Listo. El consumo de este negocio ya no se frena: avisa y queda anotado.'
        : 'Listo. Este negocio vuelve a frenar al llegar a su límite.');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardandoPolitica(false);
    }
  };
  const handleChangeUserEmail = async () => {
    if (!emailEditUser) return;
    const email = (emailEditValue || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Correo inválido');
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.patch(`/admin/tenants/${id}/users/${emailEditUser.id}/email`, {
        email,
        reason: emailEditReason || 'Cambio desde Super Admin',
      });
      toast.success(data?.message || `Correo actualizado a ${email}`);
      setEmailEditUser(null);
      setEmailEditValue('');
      setEmailEditReason('');
      await fetchTenant();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el correo');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSetAccountManager = async (userId) => {
    setSavingManager(true);
    try {
      const params = userId ? `?account_manager_id=${userId}` : '';
      await api.put(`/admin/tenants/${id}/account-manager${params}`);
      fetchTenant();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally { setSavingManager(false); }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/admin/tenants/${id}/notes?note=${encodeURIComponent(newNote.trim())}`);
      setNewNote('');
      const { data } = await api.get(`/admin/tenants/${id}/notes`);
      setNotes(data || []);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally { setAddingNote(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await api.patch(`/admin/tenants/${id}/status?status=${status}`);
      fetchTenant();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/admin/tenants/${id}/archive`);
      setShowArchiveConfirm(false);
      fetchTenant();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally { setArchiving(false); }
  };

  const handleUnarchive = async () => {
    if (!window.confirm('¿Reactivar este cliente archivado? Volverá a aparecer en la lista operativa.')) return;
    try {
      await api.post(`/admin/tenants/${id}/unarchive`);
      fetchTenant();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDeletePermanent = async () => {
    if (!tenant || deleteConfirmSlug.trim() !== tenant.slug) {
      alert('Escribe el slug exacto del tenant para confirmar');
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/tenants/${id}`, { params: { confirm_slug: deleteConfirmSlug.trim() } });
      setShowDeleteConfirm(false);
      navigate('/admin/tenants');
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally { setDeleting(false); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    setError('');
    try {
      await api.post(`/admin/tenants/${id}/users`, userForm);
      setShowAddUser(false);
      setUserForm({ email: '', password: '', name: '', tenant_role: 'tenant_user' });
      fetchTenant();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setAddingUser(false); }
  };

  const handleImpersonate = async () => {
    if (!impTarget || !impReason) return;
    setImpersonating(true);
    try {
      await startImpersonation({
        impersonated_user_id: impTarget.id,
        reason: impReason,
        mode: 'read_only',
        duration_minutes: 30,
      });
      navigate('/tenant');
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally { setImpersonating(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
    </div>
  );

  if (!tenant) return (
    <div className="text-center py-16 text-[#64748B]" data-testid="tenant-not-found">
      <p>{error || 'Tenant no encontrado'}</p>
      <Button variant="outline" onClick={() => navigate('/admin/tenants')} className="mt-4">Volver</Button>
    </div>
  );

  return (
    <div data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tenants')} data-testid="back-to-tenants" className="self-start">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] tracking-tight truncate">{tenant.name}</h1>
          <p className="text-xs sm:text-sm text-[#64748B] truncate">{tenant.slug} &middot; {tenant.vertical?.replace(/_/g, ' ')}{tenant.subtype ? ` (${tenant.subtype})` : ''} &middot; {tenant.timezone}</p>
        </div>
        <TooltipProvider>
          <div className="flex flex-wrap gap-2 self-start sm:self-auto">
            {tenant.status === 'active' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => handleStatusChange('suspended')} className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid="suspend-tenant">
                    <Pause className="w-4 h-4 mr-1" /> Suspender
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#0F172A] text-white">
                  Bloquea el acceso temporalmente. Los datos se conservan y puedes reactivarlo cuando quieras.
                </TooltipContent>
              </Tooltip>
            )}
            {tenant.status === 'suspended' && (
              <Button variant="outline" size="sm" onClick={() => handleStatusChange('active')} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid="activate-tenant">
                <Play className="w-4 h-4 mr-1" /> Reactivar
              </Button>
            )}
            {tenant.status !== 'archived' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => setShowArchiveConfirm(true)} className="text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="archive-tenant">
                    <Archive className="w-4 h-4 mr-1" /> Archivar
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#0F172A] text-white">
                  Lo saca de la operación activa (no aparece en listas normales). No borra leads, citas ni historial.
                </TooltipContent>
              </Tooltip>
            )}
            {tenant.status === 'archived' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleUnarchive} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid="unarchive-tenant">
                    <Play className="w-4 h-4 mr-1" /> Desarchivar
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#0F172A] text-white">
                  Vuelve a poner el cliente en operación normal con estado Activo.
                </TooltipContent>
              </Tooltip>
            )}
            {isPlatformOwner && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => { setDeleteConfirmSlug(''); setShowDeleteConfirm(true); }}
                    className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-tenant">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#0F172A] text-white">
                  Solo Owner. Borra el tenant y sus datos en cascada. Irreversible.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      </div>

      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle>¿Archivar a {tenant.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B] leading-relaxed">
            <strong>Suspendido</strong> bloquea el acceso por un tiempo, pero sigue visible en listas.
            <br /><br />
            <strong>Archivado</strong> lo oculta de la operación diaria sin borrar nada: conservas CRM, citas, conversaciones y facturación histórica.
            Puedes desarchivarlo después.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowArchiveConfirm(false)}>Cancelar</Button>
            <Button onClick={handleArchive} disabled={archiving} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="confirm-archive-tenant">
              {archiving ? 'Archivando…' : 'Sí, archivar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent data-testid="delete-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Eliminar permanentemente a {tenant.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B] leading-relaxed">
            Esta acción es <strong>irreversible</strong>. Se borran del sistema todos los datos del tenant
            (usuarios, CRM, citas, conversaciones, suscripción, integraciones, logs del tenant).
            <strong className="text-red-700"> No se puede recuperar ni queda registro del tenant para restaurar.</strong>
            <br /><br />
            Para confirmar, escribe el slug exacto: <code className="text-[#0F172A] font-mono bg-slate-100 px-1 rounded">{tenant.slug}</code>
          </p>
          <Input value={deleteConfirmSlug} onChange={e => setDeleteConfirmSlug(e.target.value)}
            placeholder={tenant.slug} className="text-sm font-mono" data-testid="delete-tenant-slug-input" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button onClick={handleDeletePermanent} disabled={deleting || deleteConfirmSlug.trim() !== tenant.slug}
              className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-tenant">
              {deleting ? 'Eliminando…' : 'Eliminar para siempre'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <InfoCard icon={Users} label="Usuarios" value={`${tenant.user_count}/${tenant.max_users}`} />
        <InfoCard icon={Building2} label="Sedes" value={`${tenant.location_count}/${tenant.max_locations}`} />
        <InfoCard icon={Globe} label="Zona Horaria" value={tenant.timezone} />
        <InfoCard icon={Shield} label="Plan" value={tenant.plan?.toUpperCase()} accent />
      </div>

      {/* Subscription link */}
      <div className="mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tenants/${id}/subscription`)}
          data-testid="manage-subscription-btn" className="text-[#0F172A]">
          <CreditCard className="w-4 h-4 mr-2" /> Gestionar Suscripción
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={pestanaPedida || "overview"} className="space-y-4">
        <TabsList className="bg-white border border-[#E2E8F0] rounded-lg p-1">
          <TabsTrigger value="overview" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-overview">
            General
          </TabsTrigger>
          <TabsTrigger value="users" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-users">
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-settings">
            Configuración
          </TabsTrigger>
          <TabsTrigger value="ficha360" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-ficha360">
            Ficha 360
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Nombre" value={tenant.name} />
            <DetailRow label="Slug" value={tenant.slug} />
            <DetailRow label="Vertical" value={tenant.vertical?.replace(/_/g, ' ')} />
            <DetailRow label="Sub-vertical" value={tenant.subtype || 'Sin especificar'} />
            <DetailRow label="Plan" value={tenant.plan} />
            <DetailRow label="País" value={tenant.country} />
            <DetailRow label="Moneda" value={tenant.currency} />
            <DetailRow label="Idioma" value={tenant.locale} />
            <DetailRow label="Formato de fecha" value={tenant.date_format} />
            <DetailRow label="Formato de hora" value={tenant.time_format} />
            <DetailRow label="Teléfono" value={tenant.phone} />
            <DetailRow label="Email negocio" value={tenant.email} />
            <DetailRow label="Sitio web" value={tenant.website} />
            <DetailRow label="RFC / ID Fiscal" value={tenant.tax_id} />
            <DetailRow label="Estado" value={tenant.status} />
            <DetailRow label="Onboarding" value={tenant.onboarding_completed ? 'Completado' : 'Pendiente'} />
            <DetailRow label="Creado" value={new Date(tenant.created_at).toLocaleString('es-MX')} />
          </div>
          {tenant.address && <div className="mt-4"><DetailRow label="Dirección" value={tenant.address} /></div>}
          {tenant.description && <div className="mt-4"><DetailRow label="Descripción" value={tenant.description} /></div>}

          {/* S137 (1/N) — reactivar un desarrollo suspendido. Solo desde aquí:
              el negocio puede congelarlo solo, pero descongelarlo reenciende
              cobranza y apartados, y eso pasa por la plataforma. */}
          {devsSuspendidos.length > 0 && (
            <div className="mt-6 pt-6 border-t" data-testid="devs-suspendidos">
              <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
                <Pause className="w-4 h-4 text-amber-600" />
                Desarrollos suspendidos ({devsSuspendidos.length})
              </h3>
              <p className="text-xs text-[#64748B] mt-1">
                Este negocio los congeló. Su información está intacta, pero no se venden,
                no reciben leads y nadie puede modificarlos. Solo CavernicolApp puede reactivarlos.
              </p>
              <div className="mt-3 space-y-2">
                {devsSuspendidos.map(d => (
                  <div key={d.id} className="border border-amber-200 bg-amber-50/60 rounded-lg p-3"
                    data-testid={`dev-suspendido-${d.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0F172A]">{d.name}</p>
                        <p className="text-xs text-amber-900 mt-0.5">
                          <strong>Motivo:</strong> {d.motivo_suspension || 'sin motivo registrado'}
                        </p>
                        {d.suspendido_el && (
                          <p className="text-[11px] text-amber-700 mt-0.5">
                            Suspendido el {new Date(d.suspendido_el).toLocaleString('es-MX')}
                          </p>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0"
                        onClick={() => { setReactivando(d); setMotivoReactivacion(''); }}
                        data-testid={`dev-reactivar-${d.id}`}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Reactivar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0F172A]">Usuarios del Tenant</h3>
              <Button size="sm" onClick={() => setShowAddUser(true)} className="bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="add-tenant-user-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Agregar usuario
              </Button>
            </div>
            {tenant.users?.length > 0 ? (
              <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[500px]" data-testid="tenant-users-table">
                <thead>
                  <tr className="bg-[#F8F9FA]">
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Email</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Rol</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(tenant.users || []).map(u => (
                    <tr key={u.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-[#64748B]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0] capitalize">
                          {u.tenant_role?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium border ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={async () => {
                              try {
                                const { data } = await api.post('/admin/users/unlock-login', { email: u.email });
                                toast.success(data?.message || `Login desbloqueado para ${u.email}`);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'No se pudo desbloquear');
                              }
                            }}
                            className="text-[#64748B] hover:text-amber-600"
                            data-testid={`unlock-login-${u.id}`}
                            title="Quita el bloqueo de 15 min por intentos fallidos de login"
                          >
                            <span className="text-xs">Desbloquear login</span>
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setEmailEditUser(u); setEmailEditValue(u.email || ''); setEmailEditReason(''); }}
                            className="text-[#64748B] hover:text-sky-700"
                            data-testid={`change-email-${u.id}`}
                            title="Cambiar correo de acceso (login)"
                          >
                            <span className="text-xs">Cambiar correo</span>
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setImpTarget(u); setShowImpersonate(true); }}
                            className="text-[#64748B] hover:text-[#F72585]"
                            data-testid={`impersonate-user-${u.id}`}
                          >
                            <UserCog className="w-4 h-4 mr-1" />
                            <span className="text-xs">Impersonar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <p className="text-sm text-[#64748B] py-8 text-center">Sin usuarios</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Configuración del Tenant</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Business Hours" value={JSON.stringify(tenant.business_hours || {})} />
            <DetailRow label="Contact Rules" value={JSON.stringify(tenant.contact_rules || {})} />
            <DetailRow label="Feature Flags" value={JSON.stringify(tenant.feature_flags || {})} />
          </div>
        </TabsContent>

        <TabsContent value="ficha360" className="space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3"><UserCircle className="w-4 h-4" /> Responsable asignado</h3>
            <Select value={tenant.account_manager_id || '__none__'} onValueChange={v => handleSetAccountManager(v === '__none__' ? null : v)} disabled={savingManager}>
              <SelectTrigger className="bg-white border-[#E2E8F0] text-sm max-w-sm" data-testid="account-manager-select">
                <SelectValue placeholder="Sin asignar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin asignar</SelectItem>
                {platformUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="politica-limites-card">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Al llegar a su límite</h3>
            <p className="text-xs text-[#64748B] mb-4">
              Qué hace el sistema cuando este negocio se pasa de lo que contrató.
              Es una decisión tuya, no del cliente.
            </p>
            {!politica?.opciones?.length ? (
              <p className="text-xs text-[#94A3B8]">Cargando…</p>
            ) : (
              <div className="space-y-3">
                {politica.opciones.map(op => (
                  <label
                    key={op.valor}
                    className={`flex gap-3 p-3 rounded-lg border cursor-pointer ${
                      politica.politica === op.valor
                        ? 'border-[#0F172A] bg-[#F8FAFC]'
                        : 'border-[#E2E8F0]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="politica-de-limites"
                      className="mt-1"
                      data-testid={`politica-${op.valor}`}
                      checked={politica.politica === op.valor}
                      disabled={guardandoPolitica}
                      onChange={() => cambiarPolitica(op.valor)}
                    />
                    <span>
                      <span className="block text-sm font-medium text-[#0F172A]">{op.titulo}</span>
                      <span className="block text-xs text-[#64748B] mt-1">{op.ayuda}</span>
                    </span>
                  </label>
                ))}
                {politica.politica === 'consumo_avisa' && (
                  <p className="text-xs text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-3">
                    {politica.advertencia}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="health-score-card">
              <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-1"><HeartPulse className="w-4 h-4" /> Salud del cliente</h3>
              {!health ? (
                <p className="text-xs text-[#94A3B8] text-center py-3">Cargando...</p>
              ) : (
                <>
                  <p className="text-2xl font-bold text-[#0F172A] mb-3">{health.score}<span className="text-sm font-normal text-[#94A3B8]">/{health.max_score}</span></p>
                  <div className="space-y-2">
                    {(health.factors || []).map(f => (
                      <div key={f.factor} className="flex items-center justify-between text-xs">
                        <span className="text-[#64748B] capitalize">{f.factor.replace(/_/g, ' ')} <span className="text-[#CBD5E1]">· {f.label}</span></span>
                        <span className="font-medium text-[#0F172A]">{f.points}/{f.max_points}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="tenant-tickets-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><LifeBuoy className="w-4 h-4" /> Tickets de soporte</h3>
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/tickets?tenant_id=${id}`)} data-testid="view-all-tickets-btn">
                  Ver todos
                </Button>
              </div>
              {tickets.length === 0 ? (
                <p className="text-xs text-[#94A3B8] text-center py-3">Sin tickets</p>
              ) : (
                <div className="space-y-3" data-testid="tenant-tickets-list">
                  {tickets.slice(0, 5).map(t => (
                    <div key={t.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1 cursor-pointer hover:bg-[#F8F9FA]" onClick={() => navigate(`/admin/tickets?ticket_id=${t.id}`)}>
                      <p className="text-xs font-medium text-[#0F172A]">{t.subject}</p>
                      <p className="text-[11px] text-[#94A3B8] capitalize">{t.status?.replace(/_/g, ' ')} · {t.priority}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
              <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><History className="w-4 h-4" /> Historial de Plan</h3>
              {planHistory.length === 0 ? (
                <p className="text-xs text-[#94A3B8] text-center py-3">Sin historial</p>
              ) : (
                <div className="space-y-3" data-testid="plan-history-list">
                  {planHistory.map(h => (
                    <div key={h.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1">
                      <p className="text-xs font-medium text-[#0F172A] capitalize">{h.change_type?.replace(/_/g, ' ')}</p>
                      {h.notes && <p className="text-[11px] text-[#94A3B8]">{h.notes}</p>}
                      <p className="text-[10px] text-[#CBD5E1]">{h.created_at ? new Date(h.created_at).toLocaleString('es-MX') : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
              <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><Eye className="w-4 h-4" /> Historial de Impersonación</h3>
              {impHistory.length === 0 ? (
                <p className="text-xs text-[#94A3B8] text-center py-3">Sin impersonaciones registradas</p>
              ) : (
                <div className="space-y-3" data-testid="impersonation-history-list">
                  {impHistory.map(h => (
                    <div key={h.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1">
                      <p className="text-xs font-medium text-[#0F172A]">{h.impersonator_name} → {h.impersonated_name}</p>
                      <p className="text-[11px] text-[#94A3B8]">{h.reason}</p>
                      <p className="text-[10px] text-[#CBD5E1]">
                        {h.started_at ? new Date(h.started_at).toLocaleString('es-MX') : ''}
                        {h.is_active && <span className="ml-1.5 text-emerald-600 font-medium">· activa</span>}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3"><StickyNote className="w-4 h-4" /> Notas internas</h3>
            <p className="text-xs text-[#94A3B8] mb-3">Bitácora visible solo para el equipo de la plataforma — no se puede editar ni borrar, es un historial.</p>
            <form onSubmit={handleAddNote} className="flex gap-2 mb-4">
              <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Agregar una nota..."
                className="bg-white border-[#E2E8F0] text-sm" data-testid="new-note-input" />
              <Button type="submit" disabled={addingNote || !newNote.trim()} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="add-note-btn">
                {addingNote ? 'Guardando...' : 'Agregar'}
              </Button>
            </form>
            {notes.length === 0 ? (
              <p className="text-xs text-[#94A3B8] text-center py-3">Sin notas</p>
            ) : (
              <div className="space-y-3" data-testid="tenant-notes-list">
                {notes.map(n => (
                  <div key={n.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1">
                    <p className="text-sm text-[#0F172A]">{n.note}</p>
                    <p className="text-[11px] text-[#94A3B8]">{n.author_name || 'Desconocido'} · {n.created_at ? new Date(n.created_at).toLocaleString('es-MX') : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">Agregar Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre</Label>
              <Input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} required className="bg-white border-[#E2E8F0] text-sm" data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email</Label>
              <Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} required className="bg-white border-[#E2E8F0] text-sm" data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Contraseña</Label>
              <Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} required minLength={8} className="bg-white border-[#E2E8F0] text-sm" data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Rol</Label>
              <Select value={userForm.tenant_role} onValueChange={v => setUserForm({...userForm, tenant_role: v})}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant_owner">Owner</SelectItem>
                  <SelectItem value="tenant_admin">Admin</SelectItem>
                  <SelectItem value="tenant_manager">Manager</SelectItem>
                  <SelectItem value="tenant_user">Usuario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>Cancelar</Button>
              <Button type="submit" disabled={addingUser} className="bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="submit-add-user">
                {addingUser ? 'Agregando...' : 'Agregar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Impersonation Dialog */}
      <Dialog open={showImpersonate} onOpenChange={setShowImpersonate}>
        <DialogContent className="max-w-md" data-testid="impersonation-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">Impersonar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Vas a iniciar sesión como <strong>{impTarget?.name}</strong> ({impTarget?.email}) en modo lectura.
              Todas las acciones serán registradas.
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Motivo (obligatorio)</Label>
              <Input
                value={impReason}
                onChange={e => setImpReason(e.target.value)}
                placeholder="Ej: Soporte técnico, verificación de configuración..."
                required
                minLength={5}
                className="bg-white border-[#E2E8F0] text-sm"
                data-testid="impersonation-reason"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowImpersonate(false)}>Cancelar</Button>
              <Button
                onClick={handleImpersonate}
                disabled={impersonating || impReason.length < 5}
                className="bg-[#F72585] text-white hover:bg-[#D81B60]"
                data-testid="confirm-impersonation"
              >
                {impersonating ? 'Iniciando...' : 'Iniciar Impersonación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* S137 (1/N) — reactivar pide su propio motivo, igual que suspender.
          No es simetría por gusto: reactivar reenciende cobranza y apartados
          sobre un proyecto que el negocio dio por congelado, y quien lo hace
          es alguien de la plataforma, no del negocio. */}
      <Dialog open={!!reactivando} onOpenChange={o => !o && setReactivando(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reactivar «{reactivando?.name}»</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                Al reactivarlo vuelve a aparecer en la lista del negocio, vuelve a recibir
                leads y su equipo puede modificarlo otra vez.
              </p>
              {reactivando?.motivo_suspension && (
                <p className="text-[11px] text-blue-800 mt-2">
                  <strong>Se suspendió porque:</strong> {reactivando.motivo_suspension}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">¿Por qué lo reactivas? <span className="text-red-600">*</span></Label>
              <textarea rows={3} value={motivoReactivacion}
                onChange={e => setMotivoReactivacion(e.target.value)}
                placeholder="Ejemplo: el cliente confirmó una nueva etapa de venta y pidió reabrirlo."
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                data-testid="reactivar-motivo" />
              <p className="text-[11px] text-[#94A3B8] mt-0.5">
                Queda guardado junto al motivo de la suspensión. El historial completo se conserva.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setReactivando(null)}>Cancelar</Button>
              <Button size="sm" onClick={handleReactivar} disabled={guardandoReactivacion}
                data-testid="reactivar-confirmar">
                {guardandoReactivacion ? 'Reactivando...' : 'Reactivar desarrollo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailEditUser} onOpenChange={(o) => { if (!o) setEmailEditUser(null); }}>
        <DialogContent className="max-w-md" data-testid="email-edit-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar correo de acceso</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B]">
            Usuario: <strong>{emailEditUser?.name}</strong>
          </p>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#334155]">Correo actual</label>
              <p className="text-sm text-[#0F172A]">{emailEditUser?.email}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#334155]">Correo nuevo</label>
              <Input type="email" value={emailEditValue} onChange={e => setEmailEditValue(e.target.value)}
                className="bg-white border-[#E2E8F0] text-sm" data-testid="admin-new-email-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#334155]">Motivo (opcional)</label>
              <Input value={emailEditReason} onChange={e => setEmailEditReason(e.target.value)}
                placeholder="Ej. cambió de empresa / correo corporativo"
                className="bg-white border-[#E2E8F0] text-sm" />
            </div>
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
              Se cerrará la sesión en todos sus dispositivos. Entrará con el correo nuevo y la misma contraseña.
            </p>
            <Button onClick={handleChangeUserEmail} disabled={savingEmail}
              className="w-full bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="admin-save-email-btn">
              {savingEmail ? 'Guardando…' : 'Guardar correo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function InfoCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:-translate-y-0.5 transition-all">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent ? 'text-[#F72585]' : 'text-[#64748B]'}`} strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#0F172A] tracking-tight">{value}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-1">{label}</p>
      <p className="text-sm text-[#0F172A] capitalize">{value || '-'}</p>

    </div>
  );
}
